import React, { useState } from "react";
import "./expertise.css";
import CountUp from 'react-countup';
import { Box, Typography, Grid, Paper, Stack, alpha, Container, keyframes, Modal, Fade, Backdrop, IconButton } from '@mui/material';
import SectionContainer from "../ui/SectionContainer";
import { useLanguage } from "../../i18n/LanguageContext";
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-15px) rotate(0.5deg); }
`;

const orbit = keyframes`
  from { transform: rotate(0deg) translateX(50px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(50px) rotate(-360deg); }
`;

const Expertice = ({ hideHeader = false }) => {
  const { t, lang } = useLanguage();
  const [openVideo, setOpenVideo] = useState(false);

  const stats = [
    {
      value: 2000,
      prefix: '+',
      label: t('expertise.stats.clients'),
      icon: <GroupIcon />,
      color: '#DD8748'
    },
    {
      value: 11,
      label: t('expertise.stats.experts'),
      icon: <GroupsIcon />,
      color: '#1D2558'
    },
    {
      value: 30,
      label: t('expertise.stats.experience'),
      icon: <HistoryIcon />,
      color: '#2196F3'
    },
    {
      value: 9,
      label: t('expertise.stats.solutions'),
      icon: <TipsAndUpdatesIcon />,
      color: '#4CAF50'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
      {/* Background Architecture */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: alpha('#1D2558', 0.02),
          clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ pt: 15, pb: 10, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={8} alignItems="center">
          {/* Left side: Editorial Layout */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ position: 'relative' }}>
              {!hideHeader && (
                <>
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: -60,
                      left: -20,
                      fontSize: '8rem',
                      fontWeight: 900,
                      color: alpha('#1D2558', 0.03),
                      zIndex: -1,
                      userSelect: 'none'
                    }}
                  >
                    01
                  </Typography>

                  <Typography
                    variant="overline"
                    sx={{
                      color: '#DD8748',
                      fontWeight: 900,
                      letterSpacing: 4,
                      mb: 2,fontSize: '1rem',
                      display: 'block'
                      
                    }}
                  >
                    {t('expertise.intro')}
                  </Typography>

                  <Typography
                    variant="h2"
                    sx={{
                      color: '#1D2558',
                      fontWeight: 900,
                      fontSize: { xs: '2.5rem', md: '3.8rem' },
                      lineHeight: 1.1,
                      mb: 4,
                      letterSpacing: -1.5
                    }}
                  >
                    {t('expertise.aboutTitle')}
                  </Typography>
                </>
              )}

              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.2rem',
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  borderLeft: `5px solid ${alpha('#DD8748', 0.2)}`,
                  pl: 4,
                  mb: 5,
                  mt: hideHeader ? 0 : 4
                }}
              >
                {t('expertise.description')}
              </Typography>

            </Box>
          </Grid>

          {/* Right side: Cinematic Frames */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ position: 'relative' }}>
              {/* Animated Background Element */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 120,
                  height: 120,
                  border: '1px solid #DD8748',
                  borderRadius: '50%',
                  top: -30,
                  right: -30,
                  animation: `${orbit} 10s linear infinite`,
                  zIndex: 0
                }}
              />

              {/* Main Building Frame */}
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '40px 0 40px 0',
                  overflow: 'hidden',
                  boxShadow: '0 40px 80px rgba(6, 57, 125, 0.12)',
                  zIndex: 2,
                  animation: `${float} 6s ease-in-out infinite`
                }}
              >
                <Box
                  component="img"
                  src="./images/imapoly.jpg"
                  sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>

              {/* Video "Control Center" Frame */}
              <Paper
                sx={{
                  position: 'absolute',
                  bottom: -50,
                  right: { xs: 0, sm: -40 },
                  width: { xs: '100%', sm: '320px' },
                  borderRadius: 6,
                  p: 1.5,
                  bgcolor: '#fff',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.1)',
                  zIndex: 3,
                  border: '1px solid rgba(0,0,0,0.03)'
                }}
              >
                <Box
                  onClick={() => setOpenVideo(true)}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    mb: 2,
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover .play-btn': {
                      transform: 'translate(-50%, -50%) scale(1.1)',
                      bgcolor: 'primary.main',
                      color: '#fff'
                    }
                  }}
                >
                  <video width="100%" autoPlay muted loop style={{ display: 'block', pointerEvents: 'none' }}>
                    <source src="./images/video-24-crm.mp4" type="video/mp4" />
                  </video>
                  <Box
                    className="play-btn"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                      color: 'primary.main'
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
               <Typography variant="caption" fontWeight={800} color="primary" sx={{ display: 'block', mb: 0.5, letterSpacing: 1 }}> {t('expertise.ECOSYSTÈME')}  {/*ECOSYSTÈME POLYSOFT*/}</Typography> 
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}> {t('expertise.Ready')}{/*Cloud Ready Solutions v2.4 */}
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        {/* Impact Board (Stats) - Bento Design */}
        <Box sx={{ mt: 20 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Paper
                sx={{
                  p: 6,
                  height: '100%',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #1D2558 0%, #041e42 100%)',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="overline" sx={{ color: '#DD8748', fontWeight: 900 }}>{t('expertise.NOTRE')}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 3, color: '#fff' }}>{t('expertise.performance')}</Typography>
                <Box sx={{ width: 60, height: 4, bgcolor: '#DD8748', borderRadius: 2 }} />
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                {stats.map((stat, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 8,
                        bgcolor: '#fbfcfd',
                        border: '1px solid rgba(0,0,0,0.03)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        transition: '0.3s',
                        '&:hover': {
                          bgcolor: '#fff',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      <Box sx={{
                        p: 2,
                        borderRadius: 4,
                        bgcolor: alpha(stat.color, 0.1),
                        color: stat.color,
                        '& > svg': { fontSize: 32 }
                      }}>
                        {stat.icon}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight={950} color="primary" sx={{ letterSpacing: -1 }}>
                          {stat.prefix}<CountUp end={stat.value} duration={3} enableScrollSpy />
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="text.secondary">
                          {stat.label.toUpperCase()}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Fullscreen Video Modal */}
      <Modal
        open={openVideo}
        onClose={() => setOpenVideo(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
          },
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        <Fade in={openVideo}>
          <Box sx={{
            position: 'relative',
            width: '90vw',
            maxWidth: '1200px',
            outline: 'none',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 24,
            bgcolor: '#000'
          }}>
            <IconButton
              onClick={() => setOpenVideo(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                zIndex: 2
              }}
            >
              <CloseIcon />
            </IconButton>
            <video
              width="100%"
              autoPlay
              controls
              style={{ display: 'block', maxHeight: '85vh' }}
            >
              <source src="./images/video-24-crm.mp4" type="video/mp4" />
            </video>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Expertice;
